// Textos de un premio de referido: el de la lista (lo guardado) y el resumen vivo del formulario.
import type { ReferralReward } from '../api/promotionsContract';
import type { ReferralRewardForm } from './referralPromotionForm';
import { formatCents } from './promotionsRow';
import { isCount, isMoney, toCents } from './salePromotionForm';

const percentFormat = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 });
const percentOf = (bps: number) => `${percentFormat.format(bps / 100)} %`;
const capOf = (cents: number | null) => (cents === null ? '' : ` · tope ${formatCents(cents)}`);

/** «Bono de $200», «5 % del ticket · tope $400», «Cupón de 15 % · vence en 60 días». */
export function referralRewardText(reward: ReferralReward | null): string {
    if (reward === null) return 'No gana nada';
    switch (reward.type) {
        case 'FIXED_BONUS':
            return `Bono de ${formatCents(reward.amountCents ?? 0)}`;
        case 'PERCENT_BONUS':
            return `${percentOf(reward.percentBps ?? 0)} del ticket${capOf(reward.maxAmountCents)}`;
        case 'SALE_DISCOUNT':
            return `${percentOf(reward.percentBps ?? 0)} de descuento en esta compra${capOf(reward.maxAmountCents)}`;
        case 'COUPON': {
            const coupon = reward.coupon;
            if (!coupon) return 'Cupón';
            const value =
                coupon.type === 'PERCENTAGE'
                    ? percentOf(coupon.percentBps ?? 0)
                    : formatCents(coupon.amountCents ?? 0);
            return `Cupón de ${value} · vence en ${String(coupon.validityDays)} días`;
        }
    }
}

/** El premio partido para un chip angosto: lo que gana arriba y la condición abajo. */
export function referralRewardChipParts(reward: ReferralReward): {
    label: string;
    detail?: string;
} {
    if (reward.type === 'SALE_DISCOUNT') {
        return {
            label: `${percentOf(reward.percentBps ?? 0)} de descuento`,
            detail: `En su compra${capOf(reward.maxAmountCents)}`,
        };
    }
    if (reward.type === 'COUPON' && reward.coupon) {
        const coupon = reward.coupon;
        const value =
            coupon.type === 'PERCENTAGE'
                ? percentOf(coupon.percentBps ?? 0)
                : formatCents(coupon.amountCents ?? 0);
        return {
            label: `Cupón de ${value}`,
            detail: `Vence en ${String(coupon.validityDays)} días`,
        };
    }
    return { label: referralRewardText(reward) };
}

const bps = (percent: string) => Math.round(Number(percent) * 100);

/** El resumen bajo los chips del premio; mientras falte un dato dice cuál. */
export function referralRewardFormText(form: ReferralRewardForm): string {
    const empty = { amountCents: null, percentBps: null, maxAmountCents: null, coupon: null };
    const cap = isMoney(form.maxAmount) ? toCents(form.maxAmount) : null;
    switch (form.type) {
        case 'FIXED_BONUS':
            return isMoney(form.amount)
                ? referralRewardText({
                      ...empty,
                      type: form.type,
                      amountCents: toCents(form.amount),
                  })
                : 'Falta el monto del bono';
        case 'PERCENT_BONUS':
        case 'SALE_DISCOUNT':
            return isMoney(form.percent)
                ? referralRewardText({
                      ...empty,
                      type: form.type,
                      percentBps: bps(form.percent),
                      maxAmountCents: cap,
                  })
                : 'Falta el porcentaje';
        case 'COUPON': {
            if (!isMoney(form.couponValue) || !isCount(form.couponDays))
                return 'Falta el valor del cupón';
            const isPercent = form.couponType === 'PERCENTAGE';
            return referralRewardText({
                ...empty,
                type: form.type,
                coupon: {
                    type: form.couponType,
                    amountCents: isPercent ? null : toCents(form.couponValue),
                    percentBps: isPercent ? bps(form.couponValue) : null,
                    validityDays: Number(form.couponDays),
                    scope: 'CHAIN',
                },
            });
        }
    }
}
